import * as React from "react";

import { ParcelList } from "../parcel-list/parcel-list";
import { observer } from "mobx-react";
import { Parcel } from "../../../model/parcel";
import { ParcelDetail } from "../../../model/parcel-detail";
import { ParcelFilter } from "../../../model/parcel-filter";
import { ParcelCanceledHeader } from "./parcel-canceled-header";
import { SearchListModel } from "../../../../common/component/list/search-list/search-list-model";
import {
    CanceledParcelActionType,
    ParcelStatusType,
} from "../../../constants/constants";
import { ParcelDataExporter } from "../parcel-list/parcel-data-exporter";
import { IActionCommand } from "../../../../common/component/action/action-select/bulky-action-picker";
import { parcelProcessingValidator } from "../parcel-list/parcel-processing-validator";
import { parcelsService } from "../../../services/parcels";
import { t } from "i18next";
import { SingleActionPicker } from "../../../../common/component/action/action-select/single-action-picker";
import { IWithRouterProps, withRouter } from "../../../../common/component/hoc/withRouter";
import {
    eligibleForAdministratorValidator,
} from "../../../../common/component/action/action-select/eligible-for-administrator-validator";
import { ParcelListDefaultDataProvider } from "../parcel-list/parcel-list-default-data-provider";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { context } from "../../../../context/context";
import { Permission } from "../../../../context/permission";

const parcelManagementModel = new SearchListModel<Parcel, ParcelDetail, ParcelFilter>();

interface IParcelCanceledState {
    singleActionPickerAnchorElement: HTMLElement | null;
    singleActionPickerParcelItem: Parcel | null;
}

@observer
class ParcelCanceledComponent extends ParcelList<IParcelCanceledState> {
    public state: IParcelCanceledState = {
        singleActionPickerAnchorElement: null,
        singleActionPickerParcelItem: null,
    };

    constructor(props: IWithRouterProps) {
        super(props,
            parcelManagementModel,
            new ParcelDataExporter("Canceled parcels",
                true,
                false),
            new ParcelListDefaultDataProvider("Cancelled"),
            false);
    }

    protected get parcelStatusFilter(): ParcelStatusType {
        return "Cancelled";
    }

    protected renderActions() {
        return <>
            <ParcelCanceledHeader actionsList={this.actionsList}
                                         reloadParcelsList={() => this.reloadParcelList()} />
            {this.state.singleActionPickerAnchorElement &&
                <SingleActionPicker anchorElement={this.state.singleActionPickerAnchorElement}
                                    item={this.state.singleActionPickerParcelItem}
                                    list={this.singleActionsList}
                                    onActionComplete={() => this.reloadParcelList()}
                                    onPickerClose={() => this.closeSingleActionPickerMenu()} />}
        </>;
    }

    protected get actionsList(): Array<IActionCommand<Parcel, CanceledParcelActionType>> {
        return [
            {
                execute: async parcelList => await this.executeRestoreParcels(parcelList),
                label: "parcelCanceled.action.restore.title",
                loadExecuteParam: (isScan, param) => this.loadExecuteActionParam(isScan, param),
                value: "restore",
                description: "parcelPrepareShipping.action.markReadyToShip.description",
                paramValidator: parcelProcessingValidator,
                actionEligibilityValidator: eligibleForAdministratorValidator,
            },
        ];
    }

    protected openSingleActionPickerMenu(anchor: any, item: Parcel) {
        this.setState({
            singleActionPickerAnchorElement: anchor,
            singleActionPickerParcelItem: item,
        });
    }

    private closeSingleActionPickerMenu() {
        this.setState({
            singleActionPickerParcelItem: null,
            singleActionPickerAnchorElement: null,
        });
    }

    private async executeRestoreParcels(parcelList: Parcel[]) {
        const parcelsValid: number[] = [];
        const parcelsNotValid: Array<{ parcelId: number, message: string }> = [];

        for (const parcelListItem of parcelList) {
            try {
                await parcelsService.restoreParcel(this.props.params.operationCode, +this.props.params.batchId, parcelListItem.id);
                parcelsValid.push(parcelListItem.id);
            } catch (error) {
                parcelsNotValid.push({ parcelId: parcelListItem.id, message: error.message });
            }
        }

        await this.reloadParcelList();

        if (parcelsValid.length > 0) {
            this.successMessage(parcelsValid);
        }

        if (parcelsNotValid.length > 0) {
            this.errorsMessage(parcelsNotValid);
        }
    }

    private successMessage(validParcels: number[]) {
        if (validParcels.length > 10) {
            notificationModel.addSuccessMessage(t("parcelCanceled.action.restore.successMultiple", { count: validParcels.length }));
        } else {
            notificationModel.addSuccessMessage(t("parcelCanceled.action.restore.successWithIds", { parcelsIds: validParcels.join(", ") }));
        }
    }

    private errorsMessage(parcelsNotValid: Array<{ parcelId: number, message: string }>) {
        if (parcelsNotValid.length > 10) {
            notificationModel.addErrorMessage(t("parcelCanceled.action.restore.errorMultiple", { count: parcelsNotValid.length }));
        } else {
            parcelsNotValid.map(parcelNotValid =>
                notificationModel.addErrorMessage(t("parcelCanceled.action.restore.error",
                    { id: parcelNotValid.parcelId, message: parcelNotValid.message })),
            );
        }
    }

    protected getSuccessMessage(prepareParcelShipmentAction: string, nbProcessed: number) {
        return t(`parcelCanceled.action.${prepareParcelShipmentAction}.success`, { count: nbProcessed });
    }

    protected get hiddenSortFields() {
        return ["shipmentDate"];
    }

    protected get isSelectionEnabled() {
        return context.hasPermission(Permission.FrontWrite);
    }

    protected get isParcelTrackerDisplayed() {
        return true;
    }

    protected get isPalletCodeDisplayed() {
        return false;
    }

    protected get allowOrderPendingCancellationFiltering() {
        return false;
    }

    protected get allowOrderCancellationStatusFiltering() {
        return false;
    }

    protected get allowPalletCodeFiltering() {
        return false;
    }

    protected get allowParcelCancellationStateFiltering(): boolean {
        return true;
    }
}

export const ParcelCanceled = withRouter(ParcelCanceledComponent);
